/**
 * Box "Email originale" del pannello Contesto (prototipo
 * `Centro Ticket.dc.html` L360-L376).
 *
 * Parita con `TicketDetail.tsx:462-494`: mittente, ricevuta il, inoltrato da e
 * Message-Id troncato a 24 caratteri (`inbound.ts:truncateMessageId`) con il
 * valore intero nel `title`. Le righe vuote non si mostrano, esattamente come
 * oggi. Il box vive SOLO dove c'e una email in ingresso: il backend non serve
 * `inbound` alle viste richiedente.
 */
import React, { useId, useState } from 'react';
import { Mail } from 'lucide-react';
import { truncateMessageId } from '../inbound';
import { formatDate } from '../constants';
import type { TicketDetail } from '../../../services/supportApi';

export interface EmailCardProps {
  ticket: TicketDetail;
}

const EmailCard: React.FC<EmailCardProps> = ({ ticket }) => {
  // Aperto di default (prototipo L434): e la prima cosa che si guarda su un
  // ticket nato da email.
  const [open, setOpen] = useState(true);
  const headingId = useId();

  const inbound = ticket.inbound;
  // Il box appartiene ai soli ticket nati da posta: senza `inbound` non c'e
  // niente da diagnosticare (parita TicketDetail.tsx:462).
  if (!inbound || ticket.origin !== 'email') return null;

  const rows: { k: string; v: string; title?: string }[] = [
    { k: 'MITTENTE', v: inbound.from || '' },
    { k: 'RICEVUTA IL', v: inbound.receivedAt ? formatDate(inbound.receivedAt) : '' },
    { k: 'INOLTRATO DA', v: inbound.forwardedBy || '' },
    {
      k: 'MESSAGE-ID',
      v: inbound.messageId ? truncateMessageId(inbound.messageId) : '',
      title: inbound.messageId,
    },
  ].filter((r) => r.v);

  return (
    <section className="s3-card s3-ctx" aria-labelledby={headingId}>
      <button
        type="button"
        className="s3-ctx__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Mail size={13} aria-hidden="true" className="s3-ctx__icon" />
        {/* Titolo come nel T5/T8: uno `span`, non un `h3`. Un heading dentro un
            bottone non e contenuto ammesso e i lettori di schermo lo
            appiattiscono; il nome della sezione arriva da `aria-labelledby`. */}
        <span id={headingId} className="s3-h3 s3-ctx__title">Email originale</span>
        <span className="s3-meta" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && rows.length > 0 && (
        <dl className="s3-mail__fields">
          {rows.map((r) => (
            <div key={r.k} className="s3-mail__row">
              <dt className="s3-label">{r.k}</dt>
              <dd className="s3-mail__v s3-ellipsis" title={r.title}>{r.v}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};

export default EmailCard;
